import React, { useEffect, useMemo, useState } from "react";
import Layout, { StatCard } from "@/components/Layout";
import { SCHOOL_LOGO, SCHOOL_NAME, formatTanggal, formatRupiah } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Users, GraduationCap, Wallet, AlertCircle, BookOpen, BookCheck, ListChecks, Megaphone, PiggyBank } from "lucide-react";
import KalenderDashboardWidget from "@/components/KalenderDashboardWidget";

const KALENDER_LINK = {
  kepala_sekolah: "/kepsek/kalender-pendidikan",
  guru: "/guru/kalender-pendidikan",
  orangtua: "/ortu/kalender-pendidikan",
};

export default function Dashboard({ nav, title }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pemberitahuan, setPemberitahuan] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard-stats");
        if (!cancelled) setStats(data);
      } catch (e) {
        console.error("Failed to load dashboard stats:", e);
      }
      if (user?.role === "guru" || user?.role === "orangtua") {
        try {
          const { data } = await api.get("/pemberitahuan");
          if (!cancelled) setPemberitahuan(data || []);
        } catch (e) { console.error("Failed to load pemberitahuan:", e); }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  const cards = useMemo(() => {
    if (!stats) return [];
    if (user?.role === "admin") return [
      { label: "Siswa Aktif", value: stats.siswa_aktif ?? 0, icon: GraduationCap, color: "indigo", testId: "stat-siswa" },
      { label: "Guru Aktif", value: stats.guru_aktif ?? 0, icon: Users, color: "emerald", testId: "stat-guru" },
      { label: "Kendala Pending", value: stats.kendala_pending ?? 0, icon: AlertCircle, color: "rose", testId: "stat-kendala" },
    ];
    if (user?.role === "kepala_sekolah") return [
      { label: "Siswa Aktif", value: stats.siswa_aktif ?? 0, icon: GraduationCap, color: "indigo" },
      { label: "Guru Aktif", value: stats.guru_aktif ?? 0, icon: Users, color: "emerald" },
      { label: "Sudah Bayar SPP (Bulan Ini)", value: stats.spp_sudah_bulan_ini ?? 0, icon: Wallet, color: "emerald", testId: "stat-spp-sudah" },
      { label: "Belum Bayar SPP (Bulan Ini)", value: stats.spp_belum_bulan_ini ?? 0, icon: AlertCircle, color: "rose", testId: "stat-spp-belum" },
      { label: "Pemberitahuan", value: stats.pemberitahuan ?? 0, icon: ListChecks, color: "sky" },
      { label: "Saran & Kritik", value: stats.saran_kritik ?? 0, icon: BookOpen, color: "amber" },
    ];
    if (user?.role === "guru") return [
      { label: "Siswa Pendampingan", value: stats.siswa_pendampingan ?? 0, icon: GraduationCap, color: "indigo" },
      { label: "Kendala Saya", value: stats.kendala_saya ?? 0, icon: AlertCircle, color: "rose" },
    ];
    if (user?.role === "orangtua") return [
      { label: "SPP Dibayar (Tahun Ini)", value: stats.spp_dibayar ?? 0, icon: Wallet, color: "emerald", testId: "stat-spp-dibayar" },
      { label: "Hari Hadir (Bulan Ini)", value: stats.hadir ?? 0, icon: BookCheck, color: "indigo", testId: "stat-hadir" },
      { label: "Tidak Hadir (Bulan Ini)", value: stats.tidak_hadir ?? 0, icon: AlertCircle, color: "rose", testId: "stat-tidak-hadir" },
      { label: "Saldo Tabungan", value: formatRupiah(stats.saldo_tabungan ?? 0), icon: PiggyBank, color: "sky", testId: "stat-saldo-tabungan" },
    ];
    if (user?.role === "pustaka") return [
      { label: "Total Buku", value: stats.total_buku ?? 0, icon: BookOpen, color: "indigo" },
      { label: "Permintaan Pending", value: stats.peminjaman_pending ?? 0, icon: AlertCircle, color: "amber" },
      { label: "Sedang Dipinjam", value: stats.sedang_dipinjam ?? 0, icon: BookCheck, color: "emerald" },
    ];
    return [];
  }, [stats, user?.role]);

  return (
    <Layout nav={nav}>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-testid="dashboard-title">{title}</h1>

      <section className="pp-card p-8 sm:p-12 mb-8 text-center" data-testid="welcome-card">
        <img src={SCHOOL_LOGO} alt="Logo" className="w-28 h-28 mx-auto mb-5 object-contain" />
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="welcome-text">
          Selamat Datang, {user?.nama || user?.username}
        </h2>
        <p className="text-slate-600">{SCHOOL_NAME}</p>
      </section>

      {cards.length > 0 && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
          {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </section>
      )}

      {user?.role === "kepala_sekolah" && (stats?.pendampingan_per_guru?.length ?? 0) > 0 && (
        <section className="pp-card p-5 mb-8" data-testid="dashboard-pendampingan-guru">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Siswa Pendampingan per Guru
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.pendampingan_per_guru.map((g) => (
              <div key={g.guru_id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`pendampingan-guru-${g.guru_id}`}>
                <span className="font-medium text-slate-800 text-sm truncate mr-3">{g.nama}</span>
                <span className="shrink-0 inline-flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-lg bg-indigo-100 text-indigo-700 font-bold text-sm">
                  {g.jumlah} <span className="font-normal text-xs ml-1">siswa</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="pp-card p-4 flex items-center justify-center gap-2 text-slate-600 text-sm" data-testid="today-card">
        <Calendar className="w-4 h-4" /> {today}
      </div>

      {KALENDER_LINK[user?.role] && (
        <div className="mt-6">
          <KalenderDashboardWidget linkTo={KALENDER_LINK[user.role]} />
        </div>
      )}

      {(user?.role === "guru" || user?.role === "orangtua") && pemberitahuan.length > 0 && (
        <section className="mt-8" data-testid="dashboard-pemberitahuan">
          <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><Megaphone className="w-5 h-5 text-indigo-600" /> Pemberitahuan</h3>
          <div className="space-y-3">
            {pemberitahuan.map((p) => (
              <div key={p.id} className="pp-card p-5">
                <div className="text-xs text-slate-500 mb-2">{formatTanggal(p.created_at)} — {p.posted_by_nama}</div>
                <p className="text-slate-800 whitespace-pre-wrap">{p.isi}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
